import { useState, useCallback, type ReactNode } from 'react';
import { jwtDecode } from '@/lib/jwt-decode';
import { JWT_TOKEN_KEY, USER_INFO_KEY, type UserInfo } from '@/lib/types';
import { AuthContext, type AuthContextValue } from '@/lib/auth-context-def';

export type { AuthContextValue } from '@/lib/auth-context-def';

/**
 * 初始化：从 localStorage 恢复登录状态
 * 校验 JWT 是否存在且未过期
 */
function initAuth(): { token: string | null; user: UserInfo | null; valid: boolean } {
  const token = localStorage.getItem(JWT_TOKEN_KEY);
  const userStr = localStorage.getItem(USER_INFO_KEY);

  if (!token) {
    return { token: null, user: null, valid: false };
  }

  // 校验 JWT 过期时间
  try {
    const decoded = jwtDecode<{ exp?: number }>(token);
    if (decoded.exp && decoded.exp * 1000 < Date.now()) {
      // token 过期，清理
      localStorage.removeItem(JWT_TOKEN_KEY);
      localStorage.removeItem(USER_INFO_KEY);
      return { token: null, user: null, valid: false };
    }
  } catch {
    // 无效 token，清理
    localStorage.removeItem(JWT_TOKEN_KEY);
    localStorage.removeItem(USER_INFO_KEY);
    return { token: null, user: null, valid: false };
  }

  let user: UserInfo | null = null;
  if (userStr) {
    try {
      user = JSON.parse(userStr) as UserInfo;
    } catch {
      // 忽略解析错误
    }
  }

  return { token, user, valid: true };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState(() => initAuth());

  const login = useCallback((token: string, user: UserInfo) => {
    localStorage.setItem(JWT_TOKEN_KEY, token);
    localStorage.setItem(USER_INFO_KEY, JSON.stringify(user));
    setAuthState({ token, user, valid: true });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(JWT_TOKEN_KEY);
    localStorage.removeItem(USER_INFO_KEY);
    setAuthState({ token: null, user: null, valid: false });
  }, []);

  const value: AuthContextValue = {
    isAuthenticated: authState.valid,
    user: authState.user,
    loading: false,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
